/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { timeout } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import Ember from 'ember';

import { queryEncode } from 'cio/helpers/query';
import { findOperator } from 'cio/utils/conditions-operators';
import DS from 'ember-data';
import CustomerModel from 'cio/models/customer';
import { ContextViewerFilter } from 'cio/components/action-composer/context-viewer';
import AnalyticsService from 'cio/services/analytics';
import { ComposerEditableEntityAnalyticsData } from 'cio/utils/composer/composer-editable-entity';
import AuthService from 'cio/services/auth';

const MIN_LOADING_DELAY = 250;

type Condition = any;

interface CustomerSearchArgs {
  selectedCustomer: CustomerModel;
  activeFilter: ContextViewerFilter;
  attributeCondition: Condition;
  initialMergeData: any;
  analyticsData: ComposerEditableEntityAnalyticsData;
  onSelectCustomer(customer: CustomerModel): unknown;
  onSelectActiveFilter(filter: ContextViewerFilter): unknown;
  onSelectAttributeCondition(condition: Condition): unknown;
  onLoadedCustomers(hasCustomers: boolean): unknown;
  onIsLoadingChange(isLoading: boolean): unknown;
  hasReadOnlyPermission: boolean;
}

export default class CustomerSearch extends Component<CustomerSearchArgs> {
  @service store!: DS.Store;
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;

  @tracked attributeCondition: Condition;
  @tracked mergeData: any;
  @tracked emailSearchInputValue = '';
  @tracked isEditingAttributeFilter = true;

  constructor(owner: unknown, args: CustomerSearchArgs) {
    super(owner, args);

    this.attributeCondition = args.attributeCondition;
    this.mergeData = args.initialMergeData;
  }

  lastSelectedCustomerId: string | null = this.args.selectedCustomer?.id ?? null;

  @computed('attributeCondition.operator')
  get attributeConditionOperator() {
    if (!this.attributeCondition.operator) {
      return null;
    }

    return findOperator(this.attributeCondition.operator);
  }

  @computed('attributeCondition.{field,value}', 'attributeConditionOperator')
  get isAttributeCancelDisabled() {
    return (
      isEmpty(this.attributeCondition.field) ||
      (this.attributeConditionOperator?.hasPredicate && isEmpty(this.attributeCondition.value))
    );
  }

  executeSearch = task(
    async (searchTask: CustomerSearch['globalCustomerSearch'] | CustomerSearch['attributeSearch']) => {
      let startTime = new Date().getTime();
      this.args.onIsLoadingChange(true);

      let customers = await searchTask.perform();
      this.mergeData = customers;

      let hasCustomers = !isEmpty(customers);
      this.lastSelectedCustomerId = hasCustomers ? customers.firstObject.id : null;
      this.args.onSelectCustomer(hasCustomers ? customers.firstObject : null);
      this.args.onLoadedCustomers(hasCustomers);

      let endTime = new Date().getTime();
      let elapsedTime = endTime - startTime;

      // Show a loading state for a minimum amount of time to provide feedback to
      // customers that the search ran successfully. If we don't do this, the
      // search request is sometimes too fast and it's hard to see something
      // actually changed.
      //
      // If the request itself took longer than our minimum delay time, no need to
      // add additional timeouts.
      if (elapsedTime <= MIN_LOADING_DELAY && !Ember.testing) {
        await timeout(MIN_LOADING_DELAY - elapsedTime);
      }

      this.args.onIsLoadingChange(false);
    },
  );

  globalCustomerSearch = task(async () => {
    let search = this.emailSearchInputValue;

    if (search.length === 0) {
      return this.args.initialMergeData;
    } else {
      return await this.loadCustomers({ email: search });
    }
  });

  attributeSearch = task(async () => {
    if (
      !this.attributeCondition.field ||
      (this.attributeConditionOperator?.hasPredicate && !this.attributeCondition.value)
    ) {
      return this.args.initialMergeData;
    } else {
      return await this.loadCustomers({
        filters: queryEncode([this.attributeCondition]),
      });
    }
  });

  @action
  conditionUpdate(condition: Condition) {
    this.args.onSelectAttributeCondition(condition);
    this.setIsEditingAttributeFilter(false);
    this.attributeCondition = condition;
    this.executeSearch.perform(this.attributeSearch);
  }

  @action
  conditionDelete() {
    this.clearAttributeCondition();
    this.executeSearch.perform(this.attributeSearch);
  }

  @action
  clearSearch() {
    this.emailSearchInputValue = '';
    this.executeSearch.perform(this.globalCustomerSearch);
  }

  @action
  selectFilter(filter: ContextViewerFilter) {
    this.args.onSelectActiveFilter(filter);
    this.clearAttributeCondition();
    this.emailSearchInputValue = '';

    if (filter === 'email') {
      this.executeSearch.perform(this.globalCustomerSearch);
    } else if (filter === 'attribute') {
      this.executeSearch.perform(this.attributeSearch);
    }
  }

  @action
  onSelectCustomer(customer: CustomerModel) {
    this.args.onSelectCustomer(customer);
    if (customer.id !== this.lastSelectedCustomerId) {
      this.lastSelectedCustomerId = customer.id;
      this.analytics.track('message', 'sample_data_profile_changed', this.args.analyticsData);
    }
  }

  @action
  setIsEditingAttributeFilter(isEditing: boolean) {
    this.isEditingAttributeFilter = isEditing;
  }

  private loadCustomers(params = {}) {
    this.analytics.track('message', 'sample_data_profiles_searched', this.args.analyticsData);
    return this.store.query('customer', params);
  }

  private clearAttributeCondition() {
    let condition = { operator: '=', type: 'attribute' };
    this.args.onSelectAttributeCondition(condition);
    this.setIsEditingAttributeFilter(true);
    this.attributeCondition = condition;
  }
}
